import { Button, ResultModal, StyledText } from 'UI';
import api from 'api';
import {
  CourseDto,
  CourseDtoTypeEnum,
  PaymentDtoLevelEnum,
  PaymentDtoStatusEnum,
  PaymentDtoTypeEnum,
} from 'api/generated';
import httpClient from 'api/https-client';
import { AxiosError } from 'axios';
import {
  AddPaymentMethodModal,
  BundleBuilderModal,
  BundlePaymentChoiceModal,
  BundleUpgradeOptionsModal,
  ChangePaymentMethodModal,
  ChooseProductModal,
  PaymentModal,
  Unsubscribe,
  UpgradeChooserModal,
} from 'components';
import { accessLevels } from 'constant';
import { routes } from 'constant/routes';
import { useAsyncAction, useNotifications, useToggle } from 'hooks';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css } from 'styled-components';

type CourseProviderProps = {
  children: ReactNode;
};

const CourseProvider: FC<CourseProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { errorToast, successToast } = useNotifications();
  const { boot, show } = useIntercom();
  const { isOpen: isUnsubscribeModalOpen, close: closeUnsubscribeModal, open: openUnsubscribeModal } = useToggle();
  const { isOpen: isSuccessModalOpen, close: closeSuccessModal, open: openSuccessModal } = useToggle();
  const {
    isOpen: isOpenAddPaymentMethodModal,
    open: openAddPaymentMethodModal,
    close: closeAddPaymentMethodModal,
  } = useToggle();
  const {
    isOpen: isOpenChangePaymentMethodModal,
    open: openChangePaymentMethodModal,
    close: closeChangePaymentMethodModal,
  } = useToggle();

  // Enhanced success modal that refreshes course data
  const openSuccessModalWithRefresh = () => {
    const isBundlePayment =
      courseOptionsForPayment?.type === CourseDtoTypeEnum.Bundle || (course?.type as string) === 'bundle';

    dispatch(actions.courses.closePaymentModalOpen());
    dispatch(actions.courses.closeChooseProductModalOpen());

    openSuccessModal();
    // Refresh course data after successful payment to show updated bundle status
    setTimeout(() => {
      getActiveCourse();
      getMyCourses();

      successToast(
        isBundlePayment
          ? 'Your courses have been updated! Bundle access is now active.'
          : 'Payment successful! Your course has been activated.',
      );
    }, 1000); // Small delay to ensure backend processing is complete
  };

  const closeSuccessModalWithRefresh = () => {
    dispatch(actions.courses.closePaymentModalOpen());
    dispatch(actions.courses.closeChooseProductModalOpen());
    dispatch(actions.courses.closeBundleUpgradeOptionsModal());
    dispatch(actions.courses.closeBundleBuilderModal());
    closeUnsubscribeModal();
    closeAddPaymentMethodModal();
    closeChangePaymentMethodModal();
    closeSuccessModal();

    // Also refresh when closing in case user missed the auto-refresh
    getActiveCourse();
    getMyCourses();

    // After success modal is dismissed, land user on profile without other overlays.
    navigate(routes.profile);
  };

  const accessToken = useAppSelector(selectors.auth.selectAccessToken);
  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const courses = useAppSelector(selectors.courses.selectCourses);
  const selectedCourse = useAppSelector(selectors.courses.selectCourse);
  const user = useAppSelector(selectors.user.selectUser);
  const direction = useAppSelector(selectors.direction.selectDirection);
  const isChooseProductModalOpen = useAppSelector(selectors.courses.selectIsChooseProductModalOpen);
  const isBundleUpgradeOptionsModalOpen = useAppSelector(selectors.courses.selectIsBundleUpgradeOptionsModalOpen);
  const isBundleBuilderModalOpen = useAppSelector(selectors.courses.selectIsBundleBuilderModalOpen);
  const isUpgradeChooserModalOpen = useAppSelector(selectors.courses.selectIsUpgradeChooserModalOpen);
  const upgradeChooserSourceCourseId = useAppSelector(selectors.courses.selectUpgradeChooserSourceCourseId);
  const isBundlePaymentChoiceModalOpen = useAppSelector(selectors.courses.selectIsBundlePaymentChoiceModalOpen);
  const paymentModalFromBundleChoice = useAppSelector(selectors.courses.selectPaymentModalFromBundleChoice);
  const bundleBuilderSourceCourseId = useAppSelector(selectors.courses.selectBundleBuilderSourceCourseId);
  const bundleUpgradeData = useAppSelector(selectors.courses.selectBundleUpgradeData);
  const isPaymentModalOpen = useAppSelector(selectors.courses.selectIsPaymentModalOpen);
  const courseOptionsForPayment = useAppSelector(selectors.courses.selectCourseOptionsForPayment);
  const openUnsubscribeOnChooseProductModal = useAppSelector(
    selectors.courses.selectOpenUnsubscribeOnChooseProductModal,
  );
  const isLandingPage = useAppSelector(selectors.auth.selectIsLandingPage);
  const [isDirectUnsubscribeFlow, setIsDirectUnsubscribeFlow] = useState(false);
  const [pendingRecoveryToast, setPendingRecoveryToast] = useState<string | null>(null);

  const [getActiveCourseAction] = useAsyncAction(actions.courses.getActiveCourse);
  const [getMyCoursesAction] = useAsyncAction(actions.courses.getMyCourses);
  const [getCourseByIdAction] = useAsyncAction(actions.courses.getCourseById);
  const [getCourseStatisticAction] = useAsyncAction(actions.performance.getCourseStatistic);
  const [unsubscribeCourseAction, isUnsubscribing] = useAsyncAction(actions.courses.unsubscribeCourse);
  const openChooseProductModal = () => dispatch(actions.courses.openChooseProductModalOpen());
  const closeChooseProductModal = () => {
    if (isLandingPage) {
      dispatch(actions.auth.setIsLandingPage(null));
    }
    dispatch(actions.courses.selectCourse(null));
    dispatch(actions.courses.closeChooseProductModalOpen());
  };
  const closePaymentModal = () => {
    dispatch(actions.courses.closePaymentModalOpen());
  };

  const isCreateUserPathname = window.location.pathname == '/create-user/';
  const isSubscribeToCoursePathname = window.location.pathname == '/subscribe-to-course/';

  const course = selectedCourse || activeCourse;

  const hasPausedPayment = (courseItem: CourseDto | null | undefined): boolean => {
    return Boolean(courseItem?.currentPayment?.onPause);
  };

  const lockedCourse =
    courses?.find((courseItem) => hasPausedPayment(courseItem)) ||
    (hasPausedPayment(activeCourse) ? activeCourse : null);

  const isAccessLocked = Boolean(accessToken && lockedCourse);
  const isPaymentMethodModalOpen = isOpenAddPaymentMethodModal || isOpenChangePaymentMethodModal;
  const isAccessLockVisible = isAccessLocked && !isPaymentMethodModalOpen;
  const isInstallmentLock = lockedCourse?.currentPayment?.type === PaymentDtoTypeEnum.Installment;
  const lockedBundleCourse =
    isInstallmentLock && lockedCourse
      ? courses?.find(
          (courseItem) =>
            courseItem.type === CourseDtoTypeEnum.Bundle &&
            courseItem.bundleCoursesAsBundle?.some((bundleCourse) => bundleCourse.courseId === lockedCourse.id),
        )
      : null;
  const lockedCourseForModal = lockedBundleCourse || lockedCourse;
  const isCertificationOrBundleInstallmentLock =
    isInstallmentLock &&
    (lockedCourseForModal?.type === CourseDtoTypeEnum.Certificate ||
      lockedCourseForModal?.type === CourseDtoTypeEnum.Bundle);
  const lockedCourseName = lockedCourseForModal?.name || 'your program';
  const accessLockTitle = isCertificationOrBundleInstallmentLock
    ? 'Payment Update Required to Continue Your Certification'
    : 'Access paused, quick fix below';
  const certificationLockIntro = `Your account must be up to date to continue your ${lockedCourseName} program and remain eligible for:`;
  const certificationLockBulletPoints = [
    'Taking your final exam and getting certified',
    'Access to the Fast-Track study system and IPTA guarantees',
  ];
  const certificationLockOutro =
    'Update your payment to restore full access—let’s get you certified and moving forward 🚀';
  const accessLockMessage = `Your ${lockedCourseName} study access is temporarily paused due to a failed payment.
To keep studying and stay on track, please update your payment method.
Reminder: You must be current on payments to qualify for the exam pass guarantee.
Let’s get you back in, you’ve got this 💪`;

  // currentPayment is optional in practice: a course built from /courses/:id/public (the chooser's
  // Rookie picker) has none, and reading .type off it crashed the whole provider to a white screen.
  const selectCourseIsTrial = selectedCourse?.currentPayment?.type === 'Free';

  const hasStatisticAccess =
    activeCourse?.currentPayment?.level === PaymentDtoLevelEnum.Gold ||
    activeCourse?.currentPayment?.type === PaymentDtoTypeEnum.Free;

  useEffect(() => {
    if (accessToken && !isCreateUserPathname && !isSubscribeToCoursePathname) {
      getActiveCourse();
      // Also load all user courses to support bundle upgrade options
      getMyCourses();
    }
  }, [accessToken, isCreateUserPathname, isSubscribeToCoursePathname]);

  const getActiveCourse = useCallback(async () => {
    try {
      await getActiveCourseAction();
    } catch (error) {
      console.log(error);
    }
  }, [getActiveCourseAction]);

  const getMyCourses = useCallback(async () => {
    try {
      await getMyCoursesAction();
    } catch (error) {
      console.log('Error loading courses:', error);
    }
  }, [getMyCoursesAction]);

  const dateStartPauseSubscription = activeCourse?.currentPayment.onPauseFrom;
  const dateEndPauseSubscription = activeCourse?.currentPayment.onPauseUntil;
  const isPauseCertificate = activeCourse?.currentPayment?.status === PaymentDtoStatusEnum.Trialing;
  const isInstallment = activeCourse?.currentPayment.type === PaymentDtoTypeEnum.Installment;

  const checkPause = (start: string | undefined | null, end?: string | undefined | null) => {
    if (!start || !end) {
      return;
    }
    const now = new Date();
    const startDate = new Date(start);
    const endDate = new Date(end);

    return now >= startDate && now <= endDate;
  };

  const pauseStatus = checkPause(dateStartPauseSubscription, dateEndPauseSubscription);

  useEffect(() => {
    dispatch(actions.courses.setPauseSubscription(pauseStatus));
    dispatch(actions.courses.setPauseCertificate(isPauseCertificate && isInstallment));
  }, [dispatch, pauseStatus, isPauseCertificate, isInstallment]);

  const { hasPerformance } = activeCourse?.currentPayment.level
    ? accessLevels[activeCourse.currentPayment.level]
    : accessLevels.null;

  useEffect(() => {
    if (accessToken && !isCreateUserPathname && hasStatisticAccess && activeCourse?.id) {
      getCourseStatistic(activeCourse.id);
    }
  }, [hasPerformance, accessToken, activeCourse?.id, isCreateUserPathname, hasStatisticAccess]);

  const getCourseStatistic = async (courseId?: string) => {
    const targetCourseId = courseId || activeCourse?.id;
    if (hasPerformance && !pauseStatus && targetCourseId) {
      try {
        await getCourseStatisticAction({ courseId: targetCourseId });
      } catch (error) {
        console.error('Failed to load course statistics:', error);
      }
    }
  };

  const onCloseUnsubscribeModal = () => {
    closeUnsubscribeModal();
    if (isDirectUnsubscribeFlow) {
      setIsDirectUnsubscribeFlow(false);
      return;
    }
    openChooseProductModal();
  };

  const openSupport = async () => {
    try {
      const { data: userHash } = await api.Customer.customersControllerCreateUserIntercomHash();

      boot({
        customAttributes: { custom_attribute_key: 'hi there' },
        company: { companyId: 'trainer', name: 'trainer' },
        name: user?.name,
        email: user?.email,
        userId: user?.id,
        userHash,
        utmSource: direction?.name,
        hideDefaultLauncher: true,
      });
      show();
    } catch {
      errorToast('Something went wrong');
    }
  };

  const closePaymentMethodModalWithRefresh = () => {
    closeAddPaymentMethodModal();
    closeChangePaymentMethodModal();
    void getActiveCourse();
    void getMyCourses();
  };

  const getRecoverySuccessMessage = () => {
    return 'Payment successful. Your access has been reinstated.';
  };

  const retryPaymentAfterCardUpdate = async (): Promise<{ success: boolean; message?: string }> => {
    const maxAttempts = 5;
    const delayMs = 1500;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const { data } = await httpClient.post('/customer/retryPastDuePayment');
        const attempted = Number(data?.attempted ?? 0);
        const paid = Number(data?.paid ?? 0);

        if (paid > 0) {
          setPendingRecoveryToast(getRecoverySuccessMessage());
          void getActiveCourse();
          void getMyCourses();
          return { success: true, message: 'Payment successful. Access restored.' };
        }

        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }

        void getActiveCourse();
        void getMyCourses();

        if (attempted === 0) {
          return {
            success: false,
            message: 'No payable invoice was found. Please change your payment method and retry.',
          };
        }

        return {
          success: false,
          message: 'Payment failed. Please change your payment method and retry.',
        };
      } catch (error) {
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }

        return {
          success: false,
          message: (error as AxiosError)?.message || 'Payment failed. Please change your payment method and retry.',
        };
      }
    }

    return {
      success: false,
      message: 'Payment failed. Please change your payment method and retry.',
    };
  };

  const retryPaymentWithCurrentCard = async (): Promise<{ success: boolean; message?: string }> => {
    try {
      const { data } = await httpClient.post('/customer/retryPastDuePayment');
      const attempted = Number(data?.attempted ?? 0);
      const paid = Number(data?.paid ?? 0);

      if (paid > 0) {
        setPendingRecoveryToast(getRecoverySuccessMessage());
        void getActiveCourse();
        void getMyCourses();
        return { success: true, message: 'Payment successful. Access restored.' };
      }

      await getActiveCourse();
      await getMyCourses();

      if (attempted === 0) {
        return {
          success: false,
          message: 'No payable invoice was found. Please change your payment method and retry.',
        };
      }

      return {
        success: false,
        message: 'Payment failed. Please change your payment method and retry.',
      };
    } catch (error) {
      return {
        success: false,
        message: (error as AxiosError)?.message || 'Payment failed. Please change your payment method and retry.',
      };
    }
  };

  const handleOpenPaymentMethodModal = () => {
    if (user?.paymentMethod) {
      openChangePaymentMethodModal();
      return;
    }

    openAddPaymentMethodModal();
  };

  useEffect(() => {
    if (!isAccessLocked) {
      return;
    }

    dispatch(actions.courses.closeChooseProductModalOpen());
    dispatch(actions.courses.closePaymentModalOpen());
  }, [isAccessLocked, dispatch]);

  useEffect(() => {
    if (!pendingRecoveryToast) {
      return;
    }

    if (isPaymentMethodModalOpen || isAccessLocked) {
      return;
    }

    successToast(pendingRecoveryToast);
    setPendingRecoveryToast(null);
  }, [pendingRecoveryToast, isPaymentMethodModalOpen, isAccessLocked, successToast]);

  useEffect(() => {
    if (!openUnsubscribeOnChooseProductModal || !course) {
      return;
    }

    setIsDirectUnsubscribeFlow(true);
    openUnsubscribeModal();
    dispatch(actions.courses.setOpenUnsubscribeOnChooseProductModal(false));
  }, [openUnsubscribeOnChooseProductModal, course]);

  const handleCloseBundleUpgradeModal = () => {
    dispatch(actions.courses.closeBundleUpgradeOptionsModal());
  };

  /**
   * Bundle Builder handoff. The picks have already resolved to one bundle id; fetch that course
   * and hand it to the pricing modal, which is the same flow the legacy upsell uses.
   *
   * Only one DefaultModal may be open at a time — each writes document.body.style.overflow — so
   * this closes the builder and opens the product modal in a single action.
   */
  const handleBundleBuilderContinue = async (resolved: CourseDto) => {
    const bundle = await getCourseByIdAction(resolved.id);
    if (!bundle) return;

    dispatch(actions.courses.setDisabledStatus(false));
    dispatch(actions.courses.selectCourse(bundle));
    // The simplified installments-or-lifetime step, not the full pricing table: the builder
    // already answered WHAT is being bought, and bundles sell at a single level, so the
    // three-column table would re-ask a question that no longer exists.
    dispatch(actions.courses.switchToPaymentChoiceFromBuilder());
  };

  /**
   * "Buy the bundle you're trialling" — the chooser hands over a bundle id and the learner goes
   * straight to the payment step. Re-picking four certifications to buy the package they are
   * already using would be friction with no decision behind it.
   */
  const handleChooserBuyBundle = async (bundleId: string) => {
    const bundle = await getCourseByIdAction(bundleId);
    if (!bundle) return;

    dispatch(actions.courses.setDisabledStatus(false));
    dispatch(actions.courses.selectCourse(bundle));
    dispatch(actions.courses.switchFromChooserToPaymentChoice());
  };

  const handleSelectBundleUpgradeOption = (selected: CourseDto) => {
    const isUpsellModal = bundleUpgradeData?.isUpsellModal;

    // If selecting a bundle from course upsell, ensure payment buttons are enabled
    if ((selected.type as string) === 'bundle') {
      dispatch(actions.courses.setDisabledStatus(false));
    }

    // Ensure pricing modal loads the right course
    dispatch(actions.courses.selectCourse(selected));

    if (isUpsellModal) {
      // Close bundle modal and open product modal, preserving upsell context
      dispatch(actions.courses.switchToProductModalFromUpsell());
    } else {
      dispatch(actions.courses.closeBundleUpgradeOptionsModal());
      dispatch(actions.courses.openChooseProductModalOpen());
    }
  };

  const unsubscribeFromCourse = async () => {
    try {
      if (course) {
        await unsubscribeCourseAction({ courseId: course.id });

        ('You have unsubscribed from the course');
      } else {
        throw course;
      }
      dispatch(actions.courses.setIsSuccessfulUnsubscribe(true));
    } catch (error) {
      dispatch(actions.courses.setIsSuccessfulUnsubscribe(false));
      errorToast((error as AxiosError)?.message || 'Something went wrong');
    } finally {
      setIsDirectUnsubscribeFlow(false);
      closeUnsubscribeModal();
    }
  };

  return (
    <>
      {children}

      {bundleUpgradeData && (
        <BundleUpgradeOptionsModal
          isOpen={isBundleUpgradeOptionsModalOpen}
          onClose={handleCloseBundleUpgradeModal}
          bundle={bundleUpgradeData.bundle}
          bundleCourses={bundleUpgradeData.bundleCourses}
          onSelectOption={handleSelectBundleUpgradeOption}
          isUpsellModal={bundleUpgradeData.isUpsellModal}
          upsellOrderIds={bundleUpgradeData.upsellOrderIds}
        />
      )}

      <UpgradeChooserModal
        isOpen={isUpgradeChooserModalOpen}
        onClose={() => dispatch(actions.courses.closeUpgradeChooserModal())}
        sourceCourseId={upgradeChooserSourceCourseId}
        onBuyBundle={handleChooserBuyBundle}
      />

      <BundleBuilderModal
        isOpen={isBundleBuilderModalOpen}
        onClose={() => dispatch(actions.courses.closeBundleBuilderModal())}
        sourceCourseId={bundleBuilderSourceCourseId}
        onContinue={handleBundleBuilderContinue}
      />

      <BundlePaymentChoiceModal
        isOpen={isBundlePaymentChoiceModalOpen}
        onClose={() => dispatch(actions.courses.closeBundlePaymentChoiceModal())}
        bundle={(selectedCourse?.type as string) === 'bundle' ? selectedCourse : null}
      />

      {course && (
        <ChooseProductModal
          course={course}
          isOpen={isChooseProductModalOpen}
          onRequestClose={closeChooseProductModal}
          openUnsubscribeModal={openUnsubscribeModal}
        />
      )}

      <Unsubscribe
        isOpen={isUnsubscribeModalOpen}
        onClose={onCloseUnsubscribeModal}
        onUnsubscribe={unsubscribeFromCourse}
        isLoading={isUnsubscribing}
        courseId={course?.id}
        closeChooseProductModal={closeChooseProductModal}
      />

      {courseOptionsForPayment && isPaymentModalOpen && (
        <PaymentModal
          openSuccessModal={openSuccessModalWithRefresh}
          onClose={closePaymentModal}
          onBack={
            paymentModalFromBundleChoice
              ? () => dispatch(actions.courses.switchBackToPaymentChoiceFromPayment())
              : undefined
          }
          isOpen={isPaymentModalOpen}
          courseId={courseOptionsForPayment.courseId}
          subscribeType={courseOptionsForPayment.subscribeType}
          term={courseOptionsForPayment.term}
          amount={courseOptionsForPayment.amount}
          isCertificate={courseOptionsForPayment.type === CourseDtoTypeEnum.Certificate}
          oldAmount={courseOptionsForPayment.oldAmount}
          isUpdate={courseOptionsForPayment.isUpdate}
          isActivate={course?.isActive}
          isTrial={selectCourseIsTrial}
          couponCode={courseOptionsForPayment.couponCode}
          firstMonthDiscountId={courseOptionsForPayment.firstMonthDiscountId}
          firstMonthDiscountCode={courseOptionsForPayment.firstMonthDiscountCode}
          firstMonthDiscountAmount={courseOptionsForPayment.firstMonthDiscountAmount}
          firstMonthDiscountType={courseOptionsForPayment.firstMonthDiscountType}
          firstMonthDiscountPromoCode={courseOptionsForPayment.firstMonthDiscountPromoCode}
        />
      )}
      <ResultModal
        variant="success"
        title="Payment was successful!"
        isOpen={isSuccessModalOpen}
        onClose={closeSuccessModalWithRefresh}
      />

      {isAccessLockVisible && (
        <AccessLockOverlay>
          <AccessLockModal>
            <AccessLockTitle>{accessLockTitle}</AccessLockTitle>
            {isCertificationOrBundleInstallmentLock ? (
              <>
                <AccessLockText>{certificationLockIntro}</AccessLockText>
                <AccessLockBullets>
                  {certificationLockBulletPoints.map((bulletPoint) => (
                    <AccessLockBulletText key={bulletPoint}>• {bulletPoint}</AccessLockBulletText>
                  ))}
                </AccessLockBullets>
                <AccessLockText>{certificationLockOutro}</AccessLockText>
              </>
            ) : (
              <AccessLockText>{accessLockMessage}</AccessLockText>
            )}
            <AccessLockPrimaryButton variant="primary" size="middle" onClick={handleOpenPaymentMethodModal}>
              Update Payment Method
            </AccessLockPrimaryButton>
            <AccessLockSupportButton onClick={openSupport}>Contact Support</AccessLockSupportButton>
          </AccessLockModal>
        </AccessLockOverlay>
      )}

      {isOpenAddPaymentMethodModal && (
        <AddPaymentMethodModal
          isOpen={isOpenAddPaymentMethodModal}
          onClose={closePaymentMethodModalWithRefresh}
          shouldCloseOnOverlayClick={false}
          onSuccess={retryPaymentAfterCardUpdate}
          showSuccessToast={false}
        />
      )}

      {isOpenChangePaymentMethodModal && (
        <ChangePaymentMethodModal
          isOpen={isOpenChangePaymentMethodModal}
          onClose={closePaymentMethodModalWithRefresh}
          shouldCloseOnOverlayClick={false}
          onSuccess={retryPaymentAfterCardUpdate}
          onRetryCurrentCard={retryPaymentWithCurrentCard}
          showSuccessToast={false}
        />
      )}
    </>
  );
};

export default CourseProvider;

const AccessLockOverlay = styled.div`
  ${({ theme: { colors } }) => css`
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: ${colors.neutrals[1]}63;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  `}
`;

const AccessLockModal = styled.div`
  ${({ theme: { colors } }) => css`
    width: 100%;
    max-width: 640px;
    border-radius: 16px;
    background: ${colors.neutrals[11]};
    padding: 32px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  `}
`;

const AccessLockTitle = styled(StyledText).attrs({ font: 'title_4_bold_24' })`
  ${({ theme: { colors } }) => css`
    color: ${colors.neutrals[1]};
  `}
`;

const AccessLockText = styled(StyledText).attrs({ font: 'body_large_semibold_16' })`
  ${({ theme: { colors } }) => css`
    color: ${colors.neutrals[2]};
    white-space: pre-line;
    width: 100%;
    text-align: center;
  `}
`;

const AccessLockBullets = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AccessLockBulletText = styled(StyledText).attrs({ font: 'body_large_semibold_16' })`
  ${({ theme: { colors } }) => css`
    color: ${colors.neutrals[2]};
    width: 100%;
    text-align: left;
  `}
`;

const AccessLockPrimaryButton = styled(Button)`
  width: 100%;
  max-width: 320px;
`;

const AccessLockSupportButton = styled.button`
  ${({ theme: { colors, typography } }) => css`
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    color: ${colors.primary[1]};
    ${typography.body_basic_semibold_14};
  `}
`;
